import { z } from 'zod';
import { DBClassClassIdSchema, DBClassSubjectSchema } from '../classes/schemas';
import { BillingType, EnrolmentMethod } from '../common/enums';
import { StringBooleanSchema, UnixTimestampSchema } from '../common/schemas';
import { DBCourseCourseIdSchema } from '../courses/schemas';
import { DBLessonCounterSchema, DBLessonStreamSchema } from '../lessons/schemas';
import {
  DBLessonTemplateCourseSchema,
  DBLessonTemplateLessonIdSchema,
} from '../lessonTemplates/schemas';
import { AttendanceType } from '../students/enums';
import { DBStudentAttendanceTypeSchema, DBStudentStudentIdSchema } from '../students/schemas';

/**
 * Shared & Helper Schemas
 */

/**
 * DB Schemas
 */

export const DBLessonEnrolmentStudentSchema = z.lazy(() => DBStudentStudentIdSchema);
export const DBLessonEnrolmentClassLessonIdentifierSchema = z
  .string()
  .refine(val => {
    const parts = val.split('#');
    return (
      parts.length === 5 &&
      z.lazy(() => DBClassSubjectSchema).safeParse(parts[0]).success &&
      z.lazy(() => DBClassClassIdSchema).safeParse(parts[1]).success &&
      z.lazy(() => DBLessonTemplateCourseSchema).safeParse(parts[2]).success &&
      z.lazy(() => DBLessonTemplateLessonIdSchema).safeParse(parts[3]).success &&
      z.lazy(() => DBLessonCounterSchema).safeParse(parts[4]).success
    );
  }, 'Must be in format subject#classId#course#lessonId#counter')
  .openapi({
    description:
      'A composite key combining subject, class ID, course, lesson ID, and counter, formatted as "subject#classId#course#lessonId#counter"',
    example: 'chem#lychee#chem12#HPR1#0001',
  });
export const DBLessonEnrolmentAttendanceNotifiedSchema = z.boolean();
export const DBLessonEnrolmentAttendanceTypeSchema = z.lazy(() => DBStudentAttendanceTypeSchema);
export const DBLessonEnrolmentBillingTypeSchema = z.enum(BillingType);
export const DBLessonEnrolmentCourseSchema = z.lazy(() => DBCourseCourseIdSchema);
export const DBLessonEnrolmentEnrolmentMethodSchema = z.enum(EnrolmentMethod);
export const DBLessonEnrolmentHomeworkSchema = z.object({
  complete: z.boolean().optional(),
});
export const DBLessonEnrolmentPresentSchema = z.boolean();
export const DBLessonEnrolmentRollMarkedSchema = StringBooleanSchema;
export const DBLessonEnrolmentSeparatelyBilledSchema = z.boolean();
export const DBLessonEnrolmentStartDatetimeSchema = UnixTimestampSchema;
export const DBLessonEnrolmentStreamSchema = z.lazy(() => DBLessonStreamSchema);

export const DBLessonEnrolmentSchema = z.object({
  student: DBLessonEnrolmentStudentSchema,
  classLessonIdentifier: DBLessonEnrolmentClassLessonIdentifierSchema,
  attendanceNotified: DBLessonEnrolmentAttendanceNotifiedSchema,
  attendanceType: DBLessonEnrolmentAttendanceTypeSchema,
  billingType: DBLessonEnrolmentBillingTypeSchema,
  course: DBLessonEnrolmentCourseSchema,
  enrolmentMethod: DBLessonEnrolmentEnrolmentMethodSchema,
  homework: DBLessonEnrolmentHomeworkSchema,
  present: DBLessonEnrolmentPresentSchema,
  rollMarked: DBLessonEnrolmentRollMarkedSchema,
  separatelyBilled: DBLessonEnrolmentSeparatelyBilledSchema,
  startDatetime: DBLessonEnrolmentStartDatetimeSchema,
  stream: DBLessonEnrolmentStreamSchema.optional(),
});

/**
 * Client Schemas
 */

export const LessonEnrolmentStudentSchema = DBLessonEnrolmentStudentSchema;
export const LessonEnrolmentClassIdSchema = z.object({
  subject: z.lazy(() => DBClassSubjectSchema),
  classId: z.lazy(() => DBClassClassIdSchema),
});
export const LessonEnrolmentLessonSchema = z.object({
  course: z.lazy(() => DBLessonTemplateCourseSchema),
  lessonId: z.lazy(() => DBLessonTemplateLessonIdSchema),
  counter: DBLessonCounterSchema,
});
export const LessonEnrolmentAttendanceNotifiedSchema = DBLessonEnrolmentAttendanceNotifiedSchema;
export const LessonEnrolmentAttendanceTypeSchema = DBLessonEnrolmentAttendanceTypeSchema;
export const LessonEnrolmentBillingTypeSchema = DBLessonEnrolmentBillingTypeSchema;
export const LessonEnrolmentCourseSchema = DBLessonEnrolmentCourseSchema;
export const LessonEnrolmentEnrolmentMethodSchema = DBLessonEnrolmentEnrolmentMethodSchema;
export const LessonEnrolmentHomeworkSchema = DBLessonEnrolmentHomeworkSchema;
export const LessonEnrolmentPresentSchema = DBLessonEnrolmentPresentSchema;
export const LessonEnrolmentRollMarkedSchema = z.boolean();
export const LessonEnrolmentSeparatelyBilledSchema = DBLessonEnrolmentSeparatelyBilledSchema;
export const LessonEnrolmentStartDatetimeSchema = DBLessonEnrolmentStartDatetimeSchema;
export const LessonEnrolmentStreamSchema = DBLessonEnrolmentStreamSchema;

export const LessonEnrolmentSchema = z.object({
  student: LessonEnrolmentStudentSchema,
  classId: LessonEnrolmentClassIdSchema,
  lesson: LessonEnrolmentLessonSchema,
  attendanceNotified: LessonEnrolmentAttendanceNotifiedSchema.default(false),
  attendanceType: LessonEnrolmentAttendanceTypeSchema.default(AttendanceType.InPerson),
  billingType: LessonEnrolmentBillingTypeSchema.default(BillingType.Term),
  course: LessonEnrolmentCourseSchema,
  enrolmentMethod: LessonEnrolmentEnrolmentMethodSchema.default(EnrolmentMethod.Class),
  homework: LessonEnrolmentHomeworkSchema.default({}),
  present: LessonEnrolmentPresentSchema.default(false),
  rollMarked: LessonEnrolmentRollMarkedSchema.default(false),
  separatelyBilled: LessonEnrolmentSeparatelyBilledSchema.default(false),
  startDatetime: LessonEnrolmentStartDatetimeSchema,
  stream: LessonEnrolmentStreamSchema.optional(),
});
